import React from 'react';
import { Box, Typography, styled } from '@mui/material';

import type { ReactNode } from 'react';

const CountBadge = styled(Typography)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    padding: '0 8px',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.body1.fontWeight,
    lineHeight: theme.typography.body1.lineHeight,
}));

const Container = styled('button')<{ fullWidth?: boolean; inRow?: boolean }>(
    ({ disabled, theme, fullWidth, inRow }) => ({
        flexBasis: fullWidth ? '100%' : inRow ? 'calc(33.333% - 11px)' : '48%',
        flexShrink: 0,
        display: 'flex',
        flexGrow: fullWidth ? 1 : 0,
        alignItems: 'flex-start',
        gap: 1.5,
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: 'none',
        opacity: disabled ? 0.4 : 1,
        transition: 'ease-in',
        cursor: 'pointer',
        maxHeight: '64px',
        '&:hover': {
            opacity: disabled ? 0.4 : 0.7,
        },
    })
);

type MenuButtonProps = {
    label: string;
    onClick: () => void;
    subLabel?: string;
    disabled?: boolean;
    icon?: ReactNode;
    count?: number;
    fullWidth?: boolean;
    inRow?: boolean;
};

const MenuButton = React.memo(
    ({
        label,
        onClick,
        disabled = false,
        subLabel = '',
        icon = null,
        count,
        fullWidth = false,
        inRow = false,
    }: MenuButtonProps) => {
        const handleClick = React.useCallback(() => {
            if (!disabled) {
                onClick();
            }
        }, [disabled, onClick]);

        return (
            <Container
                disabled={disabled}
                onClick={handleClick}
                fullWidth={fullWidth}
                inRow={inRow}
            >
                {icon && (
                    <Box
                        pt={0.5}
                        width="32px"
                        height="100%"
                    >
                        {icon}
                    </Box>
                )}
                <Box
                    pt={0.5}
                    display="flex"
                    flexDirection="column"
                    flexGrow={1}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                    >
                        <Typography
                            textAlign="left"
                            variant="body1"
                            color="primary"
                        >
                            {label}
                        </Typography>
                        {count !== undefined && count !== null && <CountBadge>{count}</CountBadge>}
                    </Box>
                    {subLabel && (
                        <Typography
                            textAlign="left"
                            variant="body2"
                            color="secondary"
                        >
                            {subLabel}
                        </Typography>
                    )}
                </Box>
            </Container>
        );
    }
);

MenuButton.displayName = 'MenuButton';

export default MenuButton;
